import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useToast } from "@/shared/components/ui/use-toast";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  ArrowLeft,
  Save,
  Shield,
  CheckCircle2,
  X,
} from "lucide-react";
import { Checkbox } from "@/shared/components/ui/checkbox";

// Predefined permission options (labels translated via permission.${key} in the UI)
const PERMISSION_GROUPS = {
  merchants: [
    { key: "merchants.view" },
    { key: "merchants.create" },
    { key: "merchants.edit" },
    { key: "merchants.delete" },
  ],
  engines: [
    { key: "engines.view" },
    { key: "engines.create" },
    { key: "engines.edit" },
    { key: "engines.delete" },
  ],
  resellers: [
    { key: "resellers.view" },
    { key: "resellers.create" },
    { key: "resellers.edit" },
    { key: "resellers.delete" },
  ],
  transactions: [
    { key: "transactions.view" },
    { key: "transactions.create" },
    { key: "transactions.edit" },
  ],
  staff: [
    { key: "staff.view" },
    { key: "staff.create" },
    { key: "staff.edit" },
    { key: "staff.delete" },
  ],
  settings: [
    { key: "settings.view" },
    { key: "settings.edit" },
  ],
  roles: [
    { key: "roles.view" },
    { key: "roles.create" },
    { key: "roles.edit" },
    { key: "roles.delete" },
  ],
};

export default function AdminRoleForm() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const roleId = urlParams.get("id");
  const isEdit = !!roleId;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    panelType: "ADMIN",
    permissions: [],
  });

  const [saving, setSaving] = useState(false);

  const { data: existingRole } = useQuery({
    queryKey: ["role", roleId],
    queryFn: () => {
      return adminService.getAvailableRoles().then(roles =>
        roles.find(r => r.id === roleId)
      );
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingRole) {
      setFormData(prev => ({
        ...prev,
        name: existingRole.name,
        description: existingRole.description || "",
        panelType: existingRole.panelType,
        permissions: existingRole.permissions || [],
      }));
    }
  }, [existingRole]);

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createStaffRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["available-roles"]);
      toast({
        title: "Success",
        description: "Role created successfully",
      });
      navigate(createPageUrl("AdminRoles"));
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: err?.message || "Failed to create role",
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => adminService.updateStaffRole(roleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["available-roles"]);
      toast({
        title: "Success",
        description: "Role updated successfully",
      });
      navigate(createPageUrl("AdminRoles"));
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: err?.message || "Failed to update role",
      });
    }
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePermissionToggle = (permission) => {
    if (errors.permissions) setErrors(prev => ({ ...prev, permissions: false }));
    setFormData((prev) => {
      const permissions = prev.permissions || [];
      if (permissions.includes(permission)) {
        return { ...prev, permissions: permissions.filter(p => p !== permission) };
      } else {
        return { ...prev, permissions: [...permissions, permission] };
      }
    });
  };

  const handleSelectAll = (group) => {
    if (errors.permissions) setErrors(prev => ({ ...prev, permissions: false }));
    const groupPermissions = PERMISSION_GROUPS[group].map(p => p.key);
    setFormData((prev) => {
      const current = prev.permissions || [];
      const allSelected = groupPermissions.every(p => current.includes(p));

      if (allSelected) {
        return { ...prev, permissions: current.filter(p => !groupPermissions.includes(p)) };
      } else {
        const newPermissions = [...new Set([...current, ...groupPermissions])];
        return { ...prev, permissions: newPermissions };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (formData.permissions.length === 0) newErrors.permissions = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill all required fields and select at least one permission.",
      });
      setSaving(false);
      return;
    }
    setErrors({});

    try {
      const input = {
        name: formData.name,
        description: formData.description,
        panelType: formData.panelType,
        permissions: formData.permissions,
        isSystem: false, // Optional, depending on your API
      };

      if (isEdit) {
        // Strip out panelType / isSystem if they aren't meant to be updated
        const updateInput = {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
        };
        await updateMutation.mutateAsync(updateInput);
      } else {
        await createMutation.mutateAsync(input);
      }
    } catch (error) {
      console.error("Failed to save role:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" style={{ background: 'transparent' }}>
      <style>{`
        .dark-role-form {
          --navy-950: #050d1a;
          --navy-900: #0a1628;
          --navy-800: #0f2044;
          --navy-700: #162a57;
          --navy-600: #1e3a6e;
          --navy-500: #2a4f8f;
          --navy-400: #3d6ab5;
          --accent-blue: #4f9eff;
          --accent-cyan: #00d4ff;
          --accent-glow: rgba(79, 158, 255, 0.15);
          --text-primary: #e8f0fe;
          --text-secondary: #8ba8d4;
          --text-muted: #4a6990;
          --border-color: rgba(79, 158, 255, 0.2);
          --border-hover: rgba(79, 158, 255, 0.5);
          --success: #00e5a0;
          --danger: #ff4d6d;
        }

        .dark-role-form .drf-card {
          background: linear-gradient(135deg, var(--navy-900) 0%, var(--navy-800) 100%);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(79, 158, 255, 0.1);
        }

        .dark-role-form .drf-card-header {
          padding: 24px 28px 20px;
          border-bottom: 1px solid var(--border-color);
          background: rgba(79, 158, 255, 0.04);
        }

        .dark-role-form .drf-card-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--text-primary);
          letter-spacing: 0.02em;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dark-role-form .drf-card-title::before {
          content: '';
          width: 3px;
          height: 18px;
          background: linear-gradient(180deg, var(--accent-blue), var(--accent-cyan));
          border-radius: 2px;
          flex-shrink: 0;
        }

        .dark-role-form .drf-card-desc {
          font-size: 13px;
          color: var(--text-secondary);
          margin-top: 4px;
          padding-left: 11px;
        }

        .dark-role-form .drf-card-body {
          padding: 24px 28px;
        }

        .dark-role-form .drf-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 8px;
          display: block;
        }

        .dark-role-form .drf-input {
          background: var(--navy-950) !important;
          border: 1px solid var(--border-color) !important;
          color: var(--text-primary) !important;
          border-radius: 10px !important;
          padding: 10px 14px !important;
          font-size: 14px !important;
          width: 100%;
          transition: all 0.2s ease;
          outline: none;
        }

        .dark-role-form .drf-input:focus {
          border-color: var(--accent-blue) !important;
          box-shadow: 0 0 0 3px var(--accent-glow) !important;
        }

        .dark-role-form .drf-input::placeholder {
          color: var(--text-muted) !important;
        }

        .dark-role-form .drf-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .dark-role-form .drf-textarea {
          background: var(--navy-950) !important;
          border: 1px solid var(--border-color) !important;
          color: var(--text-primary) !important;
          border-radius: 10px !important;
          padding: 10px 14px !important;
          font-size: 14px !important;
          width: 100%;
          resize: vertical;
          transition: all 0.2s ease;
          outline: none;
          font-family: inherit;
        }

        .dark-role-form .drf-textarea:focus {
          border-color: var(--accent-blue) !important;
          box-shadow: 0 0 0 3px var(--accent-glow) !important;
        }

        .dark-role-form .drf-textarea::placeholder {
          color: var(--text-muted) !important;
        }

        .dark-role-form .drf-info-box {
          background: rgba(79, 158, 255, 0.08);
          border: 1px solid rgba(79, 158, 255, 0.25);
          border-radius: 10px;
          padding: 12px 16px;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .dark-role-form .drf-info-box strong {
          color: var(--accent-blue);
        }

        .dark-role-form .drf-group-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(79, 158, 255, 0.1);
        }

        .dark-role-form .drf-group-label {
          font-size: 13px;
          font-weight: 700;
          color: var(--text-primary);
          text-transform: capitalize;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .dark-role-form .drf-group-label::before {
          content: '';
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--accent-cyan);
          flex-shrink: 0;
        }

        .dark-role-form .drf-select-all-btn {
          background: transparent;
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
          font-size: 11px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .dark-role-form .drf-select-all-btn:hover:not(:disabled) {
          border-color: var(--accent-blue);
          color: var(--accent-blue);
          background: var(--accent-glow);
        }

        .dark-role-form .drf-select-all-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .dark-role-form .drf-perm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 8px;
        }

        .dark-role-form .drf-perm-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 0.15s ease;
          background: var(--navy-950);
        }

        .dark-role-form .drf-perm-item:hover {
          border-color: var(--border-color);
          background: rgba(79, 158, 255, 0.05);
        }

        .dark-role-form .drf-perm-item.checked {
          border-color: rgba(79, 158, 255, 0.35);
          background: rgba(79, 158, 255, 0.08);
        }

        .dark-role-form .drf-perm-label {
          font-size: 13px;
          color: var(--text-secondary);
          cursor: pointer;
          flex: 1;
          user-select: none;
        }

        .dark-role-form .drf-perm-item.checked .drf-perm-label {
          color: var(--text-primary);
        }

        .dark-role-form .drf-checkbox {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 2px solid var(--text-muted);
          background: transparent;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          cursor: pointer;
        }

        .dark-role-form .drf-checkbox.checked {
          background: var(--accent-blue);
          border-color: var(--accent-blue);
        }

        .dark-role-form .drf-empty-state {
          text-align: center;
          padding: 48px 24px;
          color: var(--text-muted);
        }

        .dark-role-form .drf-empty-state svg {
          width: 40px;
          height: 40px;
          margin: 0 auto 12px;
          opacity: 0.3;
        }

        .dark-role-form .drf-summary-box {
          margin-top: 16px;
          padding: 16px 20px;
          background: rgba(79, 158, 255, 0.08);
          border: 1px solid rgba(79, 158, 255, 0.25);
          border-radius: 12px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .dark-role-form .drf-summary-box svg {
          color: var(--accent-blue);
          flex-shrink: 0;
          margin-top: 1px;
        }

        .dark-role-form .drf-summary-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .dark-role-form .drf-summary-sub {
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 2px;
        }

        .dark-role-form .drf-divider {
          height: 1px;
          background: var(--border-color);
          margin: 24px 0;
        }

        .dark-role-form .drf-btn-primary {
          background: linear-gradient(135deg, var(--accent-blue), #3a7ad5) !important;
          color: #fff !important;
          border: none !important;
          border-radius: 10px !important;
          padding: 10px 20px !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 14px rgba(79, 158, 255, 0.35);
        }

        .dark-role-form .drf-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(79, 158, 255, 0.45);
        }

        .dark-role-form .drf-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .dark-role-form .drf-btn-outline {
          background: transparent !important;
          color: var(--text-secondary) !important;
          border: 1px solid var(--border-color) !important;
          border-radius: 10px !important;
          padding: 10px 20px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .dark-role-form .drf-btn-outline:hover {
          border-color: var(--border-hover) !important;
          color: var(--text-primary) !important;
          background: var(--accent-glow) !important;
        }

        .dark-role-form .drf-btn-ghost {
          background: transparent !important;
          color: var(--text-secondary) !important;
          border: none !important;
          border-radius: 8px !important;
          padding: 8px !important;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .dark-role-form .drf-btn-ghost:hover {
          background: var(--accent-glow) !important;
          color: var(--accent-blue) !important;
        }

        .dark-role-form .drf-header-title {
          font-size: 24px;
          font-weight: 800;
          color: var(--text-primary);
          letter-spacing: -0.02em;
        }

        .dark-role-form .drf-header-sub {
          font-size: 14px;
          color: var(--text-secondary);
          margin-top: 2px;
        }

        .dark-role-form .drf-perm-group {
          padding: 20px;
          background: rgba(15, 32, 68, 0.5);
          border-radius: 12px;
          border: 1px solid rgba(79, 158, 255, 0.08);
        }
      `}</style>

      <div className="dark-role-form">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <Link to={createPageUrl("AdminRoles")}>
            <button className="drf-btn-ghost">
              <ArrowLeft style={{ width: 18, height: 18 }} />
            </button>
          </Link>
          <div>
            <h1 className="drf-header-title">
              {isEdit ? t("editRole") : t("createRole")}
            </h1>
            <p className="drf-header-sub">
              {isEdit ? t("updateRoleDetailsAndPermissions") : t("defineNewRoleWithPermissions")}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* General Information */}
            <div className="drf-card">
              <div className="drf-card-header">
                <div className="drf-card-title">{t("generalInformation")}</div>
                <div className="drf-card-desc">{t("basicDetailsForRole")}</div>
              </div>
              <div className="drf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label className="drf-label">{t("roleNameRequired")}</label>
                  <input
                    className="drf-input"
                    value={formData.name}
                    onChange={(e) => {
                      handleChange("name", e.target.value);
                      if (errors.name) setErrors(prev => ({ ...prev, name: false }));
                    }}
                    placeholder={t("merchantManagerPlaceholder")}
                    required
                    disabled={isEdit && existingRole?.isSystem}
                    style={{ border: errors.name ? '1px solid #ff4d6d' : undefined }}
                  />
                  {errors.name && <p style={{ color: '#ff4d6d', fontSize: '13px', margin: '8px 0 0 5px' }}>Name is required</p>}
                </div>

                <div>
                  <label className="drf-label">{t("description")}</label>
                  <textarea
                    className="drf-textarea"
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder={t("describeWhatRoleCanDo")}
                    rows={3}
                    disabled={isEdit && existingRole?.isSystem}
                  />
                </div>

                {!isEdit && (
                  <div>
                    <label className="drf-label">{t("panelTypeRequired")}</label>
                    <div className="drf-info-box">
                      {t("roleCreatedForAdminPanel")} <strong>{t("adminPanel")}</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Permissions */}
            <div className="drf-card" style={{ border: errors.permissions ? '1px solid #ff4d6d' : undefined }}>
              <div className="drf-card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div className="drf-card-title">{t("permissions")}</div>
                    <div className="drf-card-desc">{t("selectActionsRoleCanPerform")}</div>
                  </div>
                  {errors.permissions && (
                    <div style={{ color: '#ff4d6d', fontSize: '13px', fontWeight: '500' }}>
                      You have to select at least one!
                    </div>
                  )}
                </div>
              </div>
              <div className="drf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => {
                  const groupPermissions = permissions.map((p) => p.key);
                  const allSelected = groupPermissions.every((p) => formData.permissions.includes(p));

                  return (
                    <div key={group} className="drf-perm-group">
                      <div className="drf-group-header">
                        <span className="drf-group-label">{t(group)}</span>
                        <button
                          type="button"
                          className="drf-select-all-btn"
                          onClick={() => handleSelectAll(group)}
                          disabled={isEdit && existingRole?.isSystem}
                        >
                          {allSelected ? t("deselectAll") : t("selectAll")}
                        </button>
                      </div>

                      <div className="drf-perm-grid">
                        {permissions.map((permission) => {
                          const isChecked = formData.permissions.includes(permission.key);
                          return (
                            <label
                              key={permission.key}
                              className={`drf-perm-item ${isChecked ? 'checked' : ''}`}
                              onClick={() => !(isEdit && existingRole?.isSystem) && handlePermissionToggle(permission.key)}
                            >
                              <div className={`drf-checkbox ${isChecked ? 'checked' : ''}`}>
                                {isChecked && (
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                              <span className="drf-perm-label">
                                {t(`permission.${permission.key}`)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {formData.permissions.length === 0 && (
                  <div className="drf-empty-state">
                    <Shield />
                    <p style={{ fontSize: '14px' }}>{t("noPermissionsSelected")}</p>
                  </div>
                )}

                {formData.permissions.length > 0 && (
                  <div className="drf-summary-box">
                    <CheckCircle2 style={{ width: 18, height: 18, color: '#4f9eff' }} />
                    <div>
                      <p className="drf-summary-title">
                        {formData.permissions.length === 1
                          ? t("permissionSelected")
                          : t("permissionsSelected", { count: formData.permissions.length })}
                      </p>
                      <p className="drf-summary-sub">{t("staffWithRoleActions")}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Link to={createPageUrl("AdminRoles")}>
                <button type="button" className="drf-btn-outline">
                  {t("cancel")}
                </button>
              </Link>

              <button
                type="submit"
                disabled={saving || (isEdit && existingRole?.isSystem)}
                className="drf-btn-primary"
              >
                {saving ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Save style={{ width: 16, height: 16 }} />
                    </motion.div>
                    {t("saving")}
                  </>
                ) : (
                  <>
                    <Save style={{ width: 16, height: 16 }} />
                    {isEdit ? t("updateRole") : t("createRoleAction")}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}